// Data Quality Monitor Component - Real-time data validation display
import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, Clock, RefreshCw, Activity, Wifi, WifiOff, Settings } from 'lucide-react';
import { marketDataService } from '../services/marketData';
import { ValidationReport } from '../services/dataValidationService';
import TickerDebugInfo from './TickerDebugInfo';

interface DataQualityMonitorProps {
  isVisible?: boolean;
  onToggle?: () => void;
}

const DataQualityMonitor: React.FC<DataQualityMonitorProps> = ({ 
  isVisible = false, 
  onToggle 
}) => {
  const [validationReport, setValidationReport] = useState<ValidationReport | null>(null);
  const [healthSummary, setHealthSummary] = useState<any>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<string>('');
  const [showTickerDebug, setShowTickerDebug] = useState(false);

  // Update data quality info
  const updateDataQuality = async () => {
    try {
      const report = await marketDataService.validateDataQuality();
      const summary = marketDataService.getHealthSummary();
      
      setValidationReport(report);
      setHealthSummary(summary);
      setLastUpdate(new Date().toLocaleTimeString());
    } catch (error) {
      console.error('Error updating data quality:', error);
    }
  };

  // Force refresh data
  const handleForceRefresh = async () => {
    setIsRefreshing(true);
    try {
      const { validation } = await marketDataService.forceDataRefresh();
      setValidationReport(validation);
      setHealthSummary(marketDataService.getHealthSummary());
      setLastUpdate(new Date().toLocaleTimeString());
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Auto-update every 30 seconds
  useEffect(() => {
    if (isVisible) {
      updateDataQuality();
      const interval = setInterval(updateDataQuality, 30000);
      return () => clearInterval(interval);
    }
  }, [isVisible]);

  // Get health color
  const getHealthColor = (health: string) => {
    switch (health) {
      case 'EXCELLENT': return 'text-green-500';
      case 'GOOD': return 'text-blue-500';
      case 'WARNING': return 'text-yellow-500';
      case 'CRITICAL': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  // Get health icon
  const getHealthIcon = (health: string) => {
    switch (health) {
      case 'EXCELLENT': return <CheckCircle className="w-4 h-4" />;
      case 'GOOD': return <CheckCircle className="w-4 h-4" />;
      case 'WARNING': return <AlertTriangle className="w-4 h-4" />;
      case 'CRITICAL': return <WifiOff className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  if (!isVisible) {
    return (
      <button
        onClick={onToggle}
        className="fixed bottom-4 right-4 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg transition-colors z-50"
        title="Mostra Monitor Qualità Dati"
      >
        <Activity className="w-5 h-5" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 right-4 bg-gray-900 text-white p-4 rounded-lg shadow-xl max-w-md w-full z-50">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="w-5 h-5 text-blue-400" />
          <h3 className="font-semibold text-sm">Monitor Qualità Dati</h3>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowTickerDebug(true)}
            className="p-1 hover:bg-gray-700 rounded transition-colors"
            title="Verifica Ticker"
          >
            <Settings className="w-4 h-4" />
          </button>
          <button
            onClick={handleForceRefresh}
            disabled={isRefreshing}
            className="p-1 hover:bg-gray-700 rounded transition-colors"
            title="Aggiorna Dati"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={onToggle}
            className="p-1 hover:bg-gray-700 rounded transition-colors"
            title="Chiudi Monitor"
          >
            ×
          </button>
        </div>
      </div>

      {/* Data Source Status */}
      {healthSummary && (
        <div className="mb-3 p-2 bg-gray-800 rounded">
          <div className="flex items-center justify-between text-xs mb-1">
            <span>Fonte Dati:</span>
            <span className="font-mono uppercase">{healthSummary.currentSource}</span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span>Stato Qualità:</span>
            <div className={`flex items-center gap-1 ${getHealthColor(healthSummary.dataQuality)}`}>
              {getHealthIcon(healthSummary.dataQuality)}
              <span className="font-semibold">{healthSummary.dataQuality}</span>
            </div>
          </div>
        </div>
      )}

      {/* Real-time Status */}
      {validationReport && (
        <div className="mb-3">
          <div className="flex items-center justify-between text-xs mb-2">
            <span>Dati in Tempo Reale:</span>
            <div className="flex items-center gap-1">
              {validationReport.realTimeCount === validationReport.totalSymbols ? (
                <Wifi className="w-3 h-3 text-green-500" />
              ) : (
                <WifiOff className="w-3 h-3 text-yellow-500" />
              )}
              <span className="font-mono">
                {validationReport.realTimeCount}/{validationReport.totalSymbols}
              </span>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-700 rounded-full h-2 mb-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                validationReport.realTimeCount === validationReport.totalSymbols 
                  ? 'bg-green-500' 
                  : validationReport.realTimeCount > validationReport.totalSymbols * 0.7
                  ? 'bg-yellow-500'
                  : 'bg-red-500'
              }`}
              style={{ 
                width: `${(validationReport.realTimeCount / validationReport.totalSymbols) * 100}%` 
              }}
            />
          </div>

          {/* Detailed Stats */}
          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="text-center">
              <div className="text-green-400 font-semibold">{validationReport.realTimeCount}</div>
              <div className="text-gray-400">Real-time</div>
            </div>
            <div className="text-center">
              <div className="text-yellow-400 font-semibold">{validationReport.delayedCount}</div>
              <div className="text-gray-400">Ritardati</div>
            </div>
            <div className="text-center">
              <div className="text-red-400 font-semibold">{validationReport.errorCount}</div>
              <div className="text-gray-400">Errori</div>
            </div>
          </div>
        </div>
      )}

      {/* Problematic Symbols */}
      {healthSummary?.problematicSymbols?.length > 0 && (
        <div className="mb-3 p-2 bg-red-900/30 border border-red-500/30 rounded">
          <div className="text-xs text-red-400 mb-1">Simboli con Problemi:</div>
          <div className="flex flex-wrap gap-1">
            {healthSummary.problematicSymbols.map((symbol: string) => (
              <span key={symbol} className="text-xs bg-red-500/20 text-red-300 px-1 py-0.5 rounded">
                {symbol}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Last Update */}
      <div className="text-xs text-gray-400 text-center">
        Ultimo aggiornamento: {lastUpdate}
      </div>

      {/* Data Quality Indicator */}
      <div className="mt-2 text-center">
        {validationReport?.overallHealth === 'EXCELLENT' && (
          <div className="text-xs text-green-400">
            ✅ Tutti i dati sono in tempo reale e accurati
          </div>
        )}
        {validationReport?.overallHealth === 'GOOD' && (
          <div className="text-xs text-blue-400">
            ℹ️ La maggior parte dei dati è in tempo reale
          </div>
        )}
        {validationReport?.overallHealth === 'WARNING' && (
          <div className="text-xs text-yellow-400">
            ⚠️ Alcuni dati potrebbero essere ritardati
          </div>
        )}
        {validationReport?.overallHealth === 'CRITICAL' && (
          <div className="text-xs text-red-400">
            🚨 Problemi significativi con la qualità dei dati
          </div>
        )}
      </div>
      
      {/* Ticker Debug Modal */}
      <TickerDebugInfo 
        isVisible={showTickerDebug}
        onClose={() => setShowTickerDebug(false)}
      />
    </div>
  );
};

export default DataQualityMonitor;